import { KeyValueCustomSelect, Paragraph } from "@smartsuite/react-ui/lib";
import type { FieldChoice } from "@smartsuite/types";
import { useMemo, useRef } from "react";
import * as yup from "yup";
import { FormFieldControl } from "../../../../components/FormFieldControl/FormFieldControl";
import { useFormContext } from "../../../../hooks/useFormContext";
import { useFormField } from "../../../../hooks/useFormField";
import { isMobilePhone } from "../../../../utils/devices";
import { $t } from "../../../../utils/intl";
import type { FormFieldProps } from "../../../types";
import { applyYupDefaults, validateWithYup } from "../../../validator";
import { fieldsText } from "../../fields.text";
import type { SingleSelectFieldValue } from "../single-select.config";

import "./SingleSelectFieldControl.sass";
import {
  OTHER_OPTION_VALUE,
  OtherOptionField,
} from "../../../../components/OtherOptionField/OtherOptionField";
import { useResetOtherOptionField } from "../../../../components/OtherOptionField/OtherOptionField.hook";
import { translateOtherOption } from "../../../../components/OtherOptionField/OtherOptionField.utils";
import { ChoiceSelect } from "../../components/ChoiceSelect/ChoiceSelect";

export interface SingleSelectFieldControlProps extends FormFieldProps {
  choices?: FieldChoice[];
}

export const SingleSelectFieldControl: React.FunctionComponent<SingleSelectFieldControlProps> = ({
  field,
  formItem,
  name,
  label,
  required,
  choices,
  caption,
  readOnly,
}) => {
  const { config } = useFormContext();
  const isStatusField = field.field_type === "statusfield";
  const isSelectDisplayTypesEnabled =
    config.feature_flags?.form_select_display_types === true && !isStatusField;
  const displayType: "dropdown" | "inline" = isSelectDisplayTypesEnabled
    ? ((formItem.params?.display_type as "dropdown" | "inline" | undefined) ?? "dropdown")
    : "dropdown";
  const columnCount = (formItem.params?.number_of_columns as 1 | 2 | 3 | undefined) ?? 2;
  const dummyItemRef = useRef<HTMLSpanElement>(null);
  const formField = useFormField<SingleSelectFieldValue>({
    name,
    validate: validateWithYup(applyYupDefaults(yup.string(), field, formItem)),
  });

  // formField.value may temporarily be an array left over in localStorage from a
  // previous multi-select build of the same form; guard against non-string types
  // so .includes() does not throw.
  const isShowOtherOptionField =
    typeof formField.value === "string" && formField.value.includes(OTHER_OPTION_VALUE);

  // Options can be limited by configuration on the form builder.
  // If enabled, the choices passed in are filtered by the choices selected in the config.
  // If disabled, use either the choices passed by the config of this field type, or the
  // default choices of the actual app field.
  const options = useMemo(() => {
    const baseChoices: FieldChoice[] = field.params?.choices ?? [];

    if (formItem.params?.selections?.enabled ?? false) {
      const selectionChoicesSet = new Set(formItem.params?.selections?.choiceIDs ?? []);
      return baseChoices.filter((choice) => selectionChoicesSet.has(choice.value as string)) ?? [];
    } else {
      return baseChoices;
    }
  }, [choices, field.params?.choices, formItem.params?.selections]);

  const translatedOptions = useMemo(() => {
    return translateOtherOption(options);
  }, [options]);

  // When opening the dropdown, if the user is in a mobile device, try scrolling the dummy
  // item above the dropdown to the top of the page, so they can see options and input while
  // typing in. Selector component does not offer this behavior by default...
  const handleOpenChanged = (opened: boolean): void => {
    if (opened && isMobilePhone()) dummyItemRef.current?.scrollIntoView(true);
  };

  useResetOtherOptionField(name, isShowOtherOptionField);

  return (
    <FormFieldControl
      captionAsTooltip={formItem.params.help_text_as_tooltip}
      label={label}
      caption={caption}
      required={required}
      state={formField.state}
      errorMessage={formField.errorMessage}
      readOnly={readOnly}
      helpText={formItem.params.helpText}
    >
      {isSelectDisplayTypesEnabled ? (
        <>
          <ChoiceSelect
            formState={formField.state}
            columnCount={columnCount}
            type={displayType}
            name={name}
            options={translatedOptions}
            value={formField.value}
            disabled={readOnly}
            readOnly={readOnly}
            onChange={formField.onChange}
            onOpenedChange={handleOpenChanged}
            onChangeFocused={formField.onChangeFocused}
          />
          {displayType === "dropdown" && isShowOtherOptionField && (
            <OtherOptionField name={`metadata_${name}`} isReadOnly={readOnly} />
          )}
        </>
      ) : (
        <>
          <span ref={dummyItemRef} />
          <KeyValueCustomSelect<"value", "label", FieldChoice>
            inputBackground="white-new"
            className="form-single-select-field"
            clearIconName="close-thin"
            placeholderArrowStyle="arrow-down-smaller"
            disabled={readOnly}
            isReadOnly={readOnly}
            allowSelection={!readOnly}
            collision={isMobilePhone() ? "none" : "flipfit"}
            onChange={formField.onChange}
            onChangeFocused={formField.onChangeFocused}
            onOpenedChange={handleOpenChanged}
            options={translatedOptions}
            placeholder={$t(fieldsText.singleSelectPlaceholder)}
            searchPlaceholder={$t(fieldsText.singleSelectSearchOptionPlaceholder)}
            renderOption={(option) => (
              <div className="single-select-field-control-option">
                <Paragraph weight="medium" wordBreak="break-word" color="currentColor">
                  {option.label}
                </Paragraph>

                {option.value_help_text && (
                  <Paragraph size="xs" color="currentColor">
                    {option.value_help_text}
                  </Paragraph>
                )}
              </div>
            )}
            showFilterGreaterThan={4}
            state={formField.state}
            value={formField.value}
          />

          {isShowOtherOptionField && <OtherOptionField name={`metadata_${name}`} />}
        </>
      )}
    </FormFieldControl>
  );
};
